import { Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Partner } from '@/model';

export interface PartnesTableProps {
  data: any[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onPartnerEditClick: (partner: Partner) => void;
  onPartnerDeleteClick: (partner: Partner) => void;
}

export const PartnersTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onPartnerEditClick,
  onPartnerDeleteClick,
}: PartnesTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<Partner>();

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.partners.partners_section.components.partners_table.partners_table_created_at_header'),
    }),
    columnHelper.accessor('name', {
      cell: props => props.getValue(),
      header: t('pages.partners.partners_section.components.partners_table.partners_table_name_header'),
    }),
    columnHelper.accessor('active_actions', {
      cell: props => props.getValue(),
      header: t('pages.partners.partners_section.components.partners_table.partners_table_active_actions_header'),
      meta: {
        isNumeric: true,
      },
    }),
    columnHelper.accessor('total_reach', {
      cell: () => 0,
      header: t('pages.partners.partners_section.components.partners_table.partners_table_total_reach_header'),
      meta: {
        isNumeric: true,
      },
    }),
    columnHelper.accessor('total_impressions', {
      cell: () => 0,
      header: t('pages.partners.partners_section.components.partners_table.partners_table_total_impressions_header'),
      meta: {
        isNumeric: true,
      },
    }),
    columnHelper.accessor('total_clicks', {
      cell: () => 0,
      header: t('pages.partners.partners_section.components.partners_table.partners_table_total_clicks_header'),
      meta: {
        isNumeric: true,
      },
    }),
    columnHelper.accessor('ctr', {
      cell: () => 0,
      header: t('pages.partners.partners_section.components.partners_table.partners_table_ctr_header'),
      meta: {
        isNumeric: true,
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: t('pages.partners.partners_section.components.partners_table.partners_table_actions_header'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.partners.partners_section.components.partners_table.partners_table_actions_menu_label')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onPartnerEditClick?.(props.row.original)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='edit-2' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.partners.partners_section.components.partners_table.partners_table_edit_partner')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onPartnerDeleteClick?.(props.row.original)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.partners.partners_section.components.partners_table.partners_table_delete_partner')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={columns}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
